import { DocumentNode, FragmentDefinitionNode } from 'graphql';
import { useQuery } from '@apollo/react-hooks';
import { QueryHookOptions } from '@apollo/react-hooks/lib/types';
import { ApolloQueryResult } from 'apollo-client';
import { PageMetaContext } from '../context/PageMetaContext';
import { useNotNullableContext } from './useNotNullableContext';
import gql from 'graphql-tag';
import { QueryResult } from '@apollo/react-common';

export type UsePageQueryResult<T> = {
  notFound?: boolean;
  data?: T;
  refetch: () => Promise<ApolloQueryResult<PageQueryData<T>>>;
} & Pick<
  QueryResult<T, any>,
  'loading' | 'error' | 'startPolling' | 'stopPolling'
>;

type UsePageQueryFoundResult<T> = UsePageQueryResult<T> & {
  data: T;
};

type PageQueryData<TFragment> = {
  page?: TFragment;
};

export default function usePageQuery<TFragment>(
  fragment: DocumentNode,
  options: QueryHookOptions = {},
): UsePageQueryResult<TFragment> {
  const { pageType, pageShortName, isPreview } = useNotNullableContext(
    PageMetaContext,
  );

  const { loading, error, data, refetch, startPolling, stopPolling } = useQuery<
    PageQueryData<TFragment>
  >(fragment, {
    ...options,
    variables: {
      type: pageType.toUpperCase(),
      slug: pageShortName,
      preview: isPreview,
      ...options.variables,
    },
    ssr: false, // We do all our server side queries through our base page query
    errorPolicy: 'all',
    skip: typeof window === 'undefined',
  });
  return {
    loading,
    error,
    ...(data && data.page && { data: data.page }),
    notFound: Boolean(!loading && !error && (!data || !data.page)),
    refetch,
    startPolling,
    stopPolling,
  };
}

// eslint-disable-next-line no-redeclare
export const UsePageQueryResult = {
  hasData<T>(
    result: UsePageQueryResult<T>,
  ): result is UsePageQueryFoundResult<T> {
    return 'data' in result;
  },
};

const pageIdFragment = gql`
  fragment PageId on Page {
    id
  }
`;

export function buildQuery(...fragments: DocumentNode[]): DocumentNode {
  const fragmentDefinitions = [...fragments, pageIdFragment].map(fragment => {
    if (
      fragment.definitions.length !== 1 ||
      fragment.definitions[0].kind !== 'FragmentDefinition'
    ) {
      throw new Error(
        'The fragment DocumentNode should contain exactly one FragmentDefinition',
      );
    }

    return fragment.definitions[0] as FragmentDefinitionNode;
  });

  return {
    kind: 'Document',
    definitions: [
      ...fragmentDefinitions,
      {
        kind: 'OperationDefinition',
        operation: 'query',
        variableDefinitions: [
          {
            kind: 'VariableDefinition',
            variable: {
              kind: 'Variable',
              name: {
                kind: 'Name',
                value: 'type',
              },
            },
            type: {
              kind: 'NonNullType',
              type: {
                kind: 'NamedType',
                name: {
                  kind: 'Name',
                  value: 'PageType',
                },
              },
            },
          },
          {
            kind: 'VariableDefinition',
            variable: {
              kind: 'Variable',
              name: {
                kind: 'Name',
                value: 'slug',
              },
            },
            type: {
              kind: 'NonNullType',
              type: {
                kind: 'NamedType',
                name: {
                  kind: 'Name',
                  value: 'Slug',
                },
              },
            },
          },
          {
            kind: 'VariableDefinition',
            variable: {
              kind: 'Variable',
              name: {
                kind: 'Name',
                value: 'preview',
              },
            },
            type: {
              kind: 'NonNullType',
              type: {
                kind: 'NamedType',
                name: {
                  kind: 'Name',
                  value: 'Preview',
                },
              },
            },
          },
        ],
        selectionSet: {
          kind: 'SelectionSet',
          selections: [
            {
              kind: 'Field',
              name: {
                kind: 'Name',
                value: 'page',
              },
              arguments: [
                {
                  kind: 'Argument',
                  name: {
                    kind: 'Name',
                    value: 'type',
                  },
                  value: {
                    kind: 'Variable',
                    name: {
                      kind: 'Name',
                      value: 'type',
                    },
                  },
                },
                {
                  kind: 'Argument',
                  name: {
                    kind: 'Name',
                    value: 'slug',
                  },
                  value: {
                    kind: 'Variable',
                    name: {
                      kind: 'Name',
                      value: 'slug',
                    },
                  },
                },
                {
                  kind: 'Argument',
                  name: {
                    kind: 'Name',
                    value: 'preview',
                  },
                  value: {
                    kind: 'Variable',
                    name: {
                      kind: 'Name',
                      value: 'preview',
                    },
                  },
                },
              ],
              selectionSet: {
                kind: 'SelectionSet',
                selections: fragmentDefinitions.map(x => ({
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: x.name.value },
                })),
              },
            },
          ],
        },
      },
    ],
  };
}
